import React, { useState } from "react";
import * as styles from "./ScientistSearch.module.css";
import {
  InstantSearch,
  InfiniteHits,
  Configure,
  connectStateResults,
  connectMenu,
} from "react-instantsearch-dom";
import { searchClient, searchIndex } from "../../../lib/algolia";
import DebouncedSearchBox from "../../elements/SearchInput/DebouncedSearchBox";
import { KontentImageElement } from "../../elements/KontentImageElement/KontentImageElement";
import { Link } from "../../elements/Link/Link";

interface Props {}

interface AlgoliaMatch {
  value: string;
  matchLevel: string;
  matchedWords: string[];
}

interface SearchResult {
  objectID: string;
  url: string;
  title: string;
  description: string;
  contentCategory: string;
  firstName: string;
  lastName: string;
  image: any;
  publishDate: string;
  truncatedDate: string;
  publishMonth: number;
  publishDay: string;
  publishYear: string;
  content: any; // page content components
  contentTile: any;
  _hightlightResult: {
    url: AlgoliaMatch;
    title: AlgoliaMatch;
    description: AlgoliaMatch;
    contentCategory: string;
  };
}

interface HitItem {
  hit: SearchResult;
}

export const ScientistSearch: React.FC<Props> = ({ ...props }) => {
  const [showDateSelect, setShowDateSelect] = useState(false);
  const shortMonths = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "June",
    "July",
    "Aug",
    "Sept",
    "Oct",
    "Nov",
    "Dec",
  ];

  const handleAdvancedClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setShowDateSelect(!showDateSelect);
  };

  const ResultWrapper = connectStateResults(({ searchResults, children }) => {
    if (!searchResults?.query && !showDateSelect) {
      return null;
    }

    return searchResults && searchResults.nbHits !== 0 ? (
      <div>
        <div className={styles.resultsInfo}>
          {searchResults.nbHits} results{" "}
          {searchResults.query ? `for ${searchResults.query}` : ""}
        </div>
        <div>{children}</div>
      </div>
    ) : (
      <div className={styles.resultsInfo}>
        No results found for {searchResults?.query}{" "}
      </div>
    );
  });

  const Hit = (props: HitItem) => {
    const displayDate =
      shortMonths[props.hit.publishMonth] + " " + props.hit.publishDay;

    return (
      <div>
        <Link to={props.hit.url} target="_self">
          <div className={styles.hitLink}>
            <div className={styles.hitImage}>
              {props.hit.image?.elements?.image?.value[0]?.url && (
                <KontentImageElement
                  image={props.hit.image?.elements?.image?.value[0]}
                  alt={props.hit.image?.elements?.alt_text.value}
                  height={265}
                  width={265}
                />
              )}
            </div>
            <div className={styles.hitContent}>
              <div className={styles.hitCategory}>
                {props.hit.contentCategory &&
                  props.hit.contentCategory != "Uncategorized" && (
                    <span>{props.hit.contentCategory}</span>
                  )}
                {props.hit.publishDate && <span>: {displayDate}</span>}
              </div>
              <h3>
                <span>
                  {props.hit.firstName} {props.hit.lastName}
                </span>
              </h3>
            </div>
          </div>
        </Link>
      </div>
    );
  };

  const MenuSelect = ({
    items,
    currentRefinement,
    refine,
    translations,
  }: any) => {
    // return the DOM output
    return (
      <div className={styles.menuSelect}>
        <select
          value={currentRefinement || ""}
          onChange={(event) => refine(event.currentTarget.value)}
        >
          <option value="">{translations.seeAllOption || "See All"}</option>
          {items.map((item: any) => (
            <option
              key={item.label}
              value={item.isRefined ? currentRefinement : item.value}
            >
              {item.label}
            </option>
          ))}
        </select>
      </div>
    );
  };

  const CustomMenuSelect = connectMenu(MenuSelect);

  return (
    <div className={styles.scientistsearch}>
      <InstantSearch
        indexName={searchIndex.scientists}
        searchClient={searchClient}
      >
        <Configure hitsPerPage={4} />
        <div className={styles.searchContainer}>
          <DebouncedSearchBox placeHolder="SEARCH" delay={200} />
          <div className={styles.advancedBtnWrap}>
            <button
              onClick={handleAdvancedClick}
              className={styles.advancedBtn}
              type="button"
            >
              {showDateSelect && <span>- </span>}
              {!showDateSelect && <span>+ </span>}
              <span>SEARCH BY DATE</span>
            </button>
          </div>

          {showDateSelect && (
            <div className={styles.refinements}>
              <CustomMenuSelect
                attribute="publishDay"
                //@ts-ignore
                translations={{
                  seeAllOption: "Day",
                }}
                limit={31}
                transformItems={(items) =>
                  items.sort(
                    (leftItem: any, rightItem: any) =>
                      Number(leftItem.label) - Number(rightItem.label)
                  )
                }
              />
              <CustomMenuSelect
                attribute="publishMonth"
                limit={12}
                transformItems={(items) =>
                  items
                    .sort(
                      (leftItem: any, rightItem: any) =>
                        Number(leftItem.label) - Number(rightItem.label)
                    )
                    .map((item: any) => ({
                      ...item,
                      label: shortMonths[item.label],
                    }))
                }
                //@ts-ignore
                translations={{
                  seeAllOption: "Month",
                }}
              />
              <CustomMenuSelect
                attribute="publishYear"
                //@ts-ignore
                translations={{
                  seeAllOption: "Year",
                }}
                transformItems={(items) =>
                  items.sort(
                    (leftItem: any, rightItem: any) =>
                      Number(leftItem.label) - Number(rightItem.label)
                  )
                }
              />
            </div>
          )}
        </div>
        <div>
          <ResultWrapper>
            <InfiniteHits
              hitComponent={Hit}
              translations={{ loadMore: "Show More" }}
            />
          </ResultWrapper>
        </div>
      </InstantSearch>
    </div>
  );
};
